import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import Process from "@/components/Process";
import QuickCommerce from "@/components/QuickCommerce";
import ContactCTA from "@/components/ContactCTA";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";

const ease = [0.22, 1, 0.36, 1];

const CAPABILITIES = [
    {
        no: "01",
        title: "Branding",
        body: "Strategy, positioning, verbal identity, visual identity and full brand systems designed to grow — from a founder-led seed to a mature identity that scales across every touchpoint.",
        includes: [
            "Brand strategy & positioning",
            "Naming & verbal identity",
            "Logo, mark & typography",
            "Design system & guidelines",
        ],
    },
    {
        no: "02",
        title: "Websites",
        body: "Editorial, high-conversion websites — from single-page launches to full multi-page marketing sites and e-commerce storefronts, built for speed, SEO and a story that sells.",
        includes: [
            "Website design & development",
            "E-commerce (Shopify / custom)",
            "Landing pages & CRO",
            "Content management & handover",
        ],
    },
    {
        no: "03",
        title: "AI",
        body: "AI-assisted creative production and workflows — from concept to output, we help brands compress creative cycles without losing craft or consistency.",
        includes: [
            "AI content & creative production",
            "Generative asset pipelines",
            "AI-assisted copywriting",
            "Workflow automation",
        ],
    },
    {
        no: "04",
        title: "Performance Marketing",
        body: "Full-funnel paid media across Meta, Google, YouTube and programmatic — engineered around clean attribution, weekly creative iteration and ROAS that holds at scale.",
        includes: [
            "Meta, Google & YouTube Ads",
            "Performance creative",
            "Attribution & measurement",
            "Landing pages & CRO",
        ],
    },
    {
        no: "05",
        title: "Commerce Growth",
        body: "End-to-end enablement across Quick Commerce and Marketplaces — Blinkit, Zepto, Instamart, Amazon, Flipkart and beyond. Onboarding, cataloguing, media and margins.",
        includes: [
            "Q-comm & marketplace onboarding",
            "Catalog optimisation",
            "Marketplace performance media",
            "Data & margin analytics",
        ],
    },
];

const ENGAGEMENTS = [
    {
        label: "Project",
        title: "One outcome, sharp scope",
        body: "A defined deliverable with a fixed timeline. Rebrand, website, launch campaign, product-drop content sprint.",
    },
    {
        label: "Retainer",
        title: "Always-on partnership",
        body: "A monthly cadence across strategy, creative and media. Best for growing brands that need consistent output and a senior team on speed dial.",
    },
    {
        label: "Growth Sprint",
        title: "8-week focused push",
        body: "A time-boxed sprint to unlock a specific metric — new-market launch, category expansion, a stubborn plateau. Diagnose, ship, measure, decide.",
    },
];

export default function Services() {
    return (
        <>
            <Seo {...SEO.services} />
            <PageHero
                data-testid="services-page-hero"
                kicker="/ Services · 01"
                meta="One methodology · five specialisms"
                title="What we do."
                accent="How we do it"
                tagline="A single, senior team across strategy, brand, build and growth. Five specialisms — one continuous process."
            />

            {/* Process (shared with home for methodology consistency) */}
            <Process />

            {/* Capabilities */}
            <section
                data-testid="services-capabilities"
                className="relative py-24 lg:py-32"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 lg:mb-20">
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                (Capabilities / 02)
                            </div>
                            <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl">
                                Five capabilities.
                                <br />
                                <span className="text-olive">One growth partner.</span>
                            </h2>
                        </div>
                        <p className="font-serif italic text-xl lg:text-2xl text-black/60 max-w-md">
                            No handoffs, no agency-of-record chaos. Strategy,
                            craft and media in the same room.
                        </p>
                    </div>

                    <div className="border-t border-black/15">
                        {CAPABILITIES.map((c, i) => (
                            <motion.article
                                key={c.no}
                                data-testid={`capability-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.8,
                                    ease,
                                    delay: i * 0.06,
                                }}
                                className="relative grid grid-cols-12 gap-y-6 lg:gap-x-10 py-12 lg:py-20 border-b border-black/15 group transition-[background-color,border-color] duration-200 ease-out hover:bg-black/[0.02] hover:border-olive/30"
                            >
                                {/* Olive accent line on hover */}
                                <span
                                    aria-hidden
                                    className="absolute left-0 top-0 bottom-0 w-[3px] bg-olive origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ease-out"
                                />

                                <div className="col-span-12 lg:col-span-2 lg:pl-6">
                                    <div className="font-display font-black text-3xl lg:text-4xl text-olive transition-transform duration-200 group-hover:-translate-y-[3px]">
                                        {c.no}
                                    </div>
                                </div>
                                <div className="col-span-12 lg:col-span-5">
                                    <h3 className="font-display font-black uppercase tracking-tight text-3xl lg:text-4xl leading-tight transition-transform duration-200 group-hover:-translate-y-[3px]">
                                        {c.title}
                                    </h3>
                                    <p className="mt-5 text-black/70 leading-[1.7] max-w-lg">
                                        {c.body}
                                    </p>
                                </div>
                                <div className="col-span-12 lg:col-span-5 lg:pt-1">
                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/45 mb-5">
                                        What&apos;s inside
                                    </div>
                                    <ul className="grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
                                        {c.includes.map((item) => (
                                            <li
                                                key={item}
                                                className="text-sm text-black/75 leading-relaxed flex items-start gap-3"
                                            >
                                                <span className="inline-block w-2 h-px bg-olive mt-[10px] shrink-0" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quick Commerce spotlight (reused) */}
            <QuickCommerce />

            {/* Engagement models */}
            <section
                data-testid="services-engagement"
                className="relative py-24 lg:py-32 bg-bone"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                        (Engagement models / 04)
                    </div>
                    <h2 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
                        Three ways to work with us.
                    </h2>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 border-t border-black/15">
                        {ENGAGEMENTS.map((e, i) => (
                            <motion.div
                                key={e.label}
                                data-testid={`engagement-${e.label.toLowerCase().replace(/\s+/g, "-")}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.8,
                                    ease,
                                    delay: i * 0.08,
                                }}
                                className={`py-10 lg:py-14 ${
                                    i !== ENGAGEMENTS.length - 1
                                        ? "border-b md:border-b-0 md:border-r"
                                        : ""
                                } border-black/15 md:pr-8 md:pl-8 first:md:pl-0 last:md:pr-0 group`}
                            >
                                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#2E5019]">
                                    {e.label}
                                </div>
                                <h3 className="mt-4 font-display font-bold text-2xl lg:text-3xl tracking-tight">
                                    {e.title}
                                </h3>
                                <p className="mt-4 text-black/70 leading-relaxed">
                                    {e.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <ContactCTA
                kicker="Ready to plant something?"
                title="Let's find your first step."
                body="Not sure which capability you need first? Message us — we'll help you scope it in one WhatsApp thread."
            />
        </>
    );
}
